import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "@/lib/site";
import { useBooking } from "@/components/BookingProvider";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/owners", label: "For Owners" },
  { to: "/buyers", label: "For Buyers" },
  { to: "/workshops", label: "Workshops" },
];

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (p) => location.pathname === p;

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#E2E8F0] shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Cultural Capital" className="h-9 w-auto" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-lg font-semibold text-ink">Cultural Capital</span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-brand mt-1">Transition Intelligence</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5 lg:gap-7">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`nav-underline text-sm tracking-wide transition-colors ${
                  isActive(l.to) ? "text-ink font-semibold active" : "text-[#475569] hover:text-ink"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={openBooking}
              data-testid="nav-book-cta"
              className="group inline-flex items-center gap-2 bg-brand text-white px-4 lg:px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-brand-deep transition-colors whitespace-nowrap"
            >
              Book Strategy Call
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>

          <button
            className="md:hidden p-2 text-ink"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#FDFBF7] border-t border-[#E2E8F0] px-5 py-6 space-y-4 animate-fade-in">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className={`block text-base ${isActive(l.to) ? "text-ink font-semibold" : "text-[#475569]"}`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => { setMobileOpen(false); openBooking(); }}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white px-5 py-3 text-sm font-medium"
          >
            Book Strategy Call
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
